import { useForm } from 'react-hook-form';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { Button, Input } from '@eombor/ui';
import { zodResolver } from '../../../shared/form/zod-resolver.js';
import { motionEase, motionSeconds } from '../../../shared/ui/motion.js';
import { ThemeToggle } from '../../../shared/theme/ThemeToggle.js';
import { LocalePicker } from '../../../shared/i18n/LocalePicker.js';
import { BrandMark } from '../../../shared/brand/BrandMark.js';
import { PasswordInput } from '../../../shared/ui/PasswordInput.js';
import { t } from '../../../shared/i18n/index.js';
import { loginFormSchema, type LoginFormValues } from '../model/login-form.js';
import type { CaptchaState } from '../model/useCaptcha.js';
import { LoginVisual } from './LoginVisual.js';
import { CaptchaField } from './CaptchaField.js';
import { TelegramLoginButton } from './TelegramLoginButton.js';

export interface LoginPageProps {
  onSubmit: (values: LoginFormValues) => void;
  /** Serverdan kelgan xato matni — allaqachon tarjima qilingan, shundayligicha ko'rsatiladi. */
  error?: string;
  loading?: boolean;
  /**
   * CAPTCHA holati — sahifada BITTA va uni ikkala kirish yo'li ham
   * ishlatadi. Tarmoq ishini `LoginRoute` bajaradi, sahifa esa faqat
   * chizadi: shu tufayli sahifaning sinovi tarmoqqa bog'lanmaydi.
   */
  captcha: CaptchaState;
}

/**
 * Har kuni ertalab ko'riladigan birinchi ekran. Sof ko'rinish komponenti:
 * tarmoqqa chiqmaydi, do'konni bilmaydi — yuborishni `onSubmit` bajaradi.
 *
 * Ikki panel: chapda vizual (`LoginVisual`, `lg` dan boshlab), o'ngda forma.
 * Kichik ekranda chap panel butunlay yo'qoladi va forma butun enni oladi —
 * omborda planshet va telefondan ham kiriladi.
 */
export const LoginPage = ({ onSubmit, error, loading = false, captcha }: LoginPageProps) => {
  const reduceMotion = useReducedMotion() === true;

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginFormSchema),
    defaultValues: { username: '', password: '' },
  });

  const usernameField = register('username');

  const bannerTransition = {
    duration: reduceMotion ? 0 : motionSeconds('--motion-base', 220),
    ease: motionEase('--ease-out-quick', [0.16, 1, 0.3, 1]),
  };

  /*
   * `lg:flex-row-reverse` — katta ekranda forma CHAPDA, quyuq panel o'ngda.
   * Bank sahifalarining odatiy tartibi shu: ko'z chapdan boshlaydi va u yerda
   * ish bo'lishi kerak, panel esa fon. Kichik ekranda panel umuman
   * ko'rinmaydi, shuning uchun tartib faqat `lg` dan boshlanadi.
   */
  return (
    <div className="bg-bg text-text flex min-h-dvh lg:flex-row-reverse">
      <LoginVisual />

      {/* O'ng tomon ATAYLAB tekis: vizual og'irlik faqat chap panelda turadi.
          Ilgari bu yerda sahifa bo'ylab `LoginBackdrop` naqshi turardi va u
          forma bilan e'tibor uchun kurashardi. */}
      <main className="bg-bg relative flex min-h-dvh flex-1 flex-col px-5 py-6 sm:px-10 sm:py-8">
        {/* Tema va til — sahifaning yuqori o'ng burchagida, formadan tashqarida:
            ular kirishning bir qismi emas, ekranning sozlamasi. */}
        <div className="relative flex items-center justify-end gap-2">
          <ThemeToggle withLabel={false} />
          <LocalePicker />
        </div>

        <div className="relative flex flex-1 items-center justify-center py-8">
          <form
            noValidate
            onSubmit={(event) => {
              void handleSubmit((values) => {
                onSubmit(values);
              })(event);
            }}
            className="w-full max-w-[25rem]"
          >
            {/* Chap panel yashiringan ekranda brend faqat shu yerda ko'rinadi. */}
            <div className="mb-8 flex items-center gap-3 lg:hidden">
              {/* Rang tokendan: yorug' temada quyuq yashil, qorong'ida ochroq. */}
              <BrandMark className="text-accent size-9" />
              <div className="min-w-0">
                <p className="text-lg font-semibold tracking-tight">{t('app.name')}</p>
                <p className="text-text-muted text-xs">{t('login.brandTagline')}</p>
              </div>
            </div>

            <h1 className="text-2xl font-semibold tracking-tight">{t('login.welcome')}</h1>
            <p className="text-text-muted mt-1.5 text-sm text-pretty">{t('login.subtitle')}</p>

            <AnimatePresence initial={false}>
              {error !== undefined && error !== '' ? (
                <motion.p
                  key="login-error"
                  role="alert"
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={bannerTransition}
                  className="border-danger/30 bg-danger-soft text-danger mt-6 rounded-[var(--radius-sm)] border px-3 py-2 text-sm"
                >
                  {error}
                </motion.p>
              ) : null}
            </AnimatePresence>

            <div className="mt-7 flex flex-col gap-4">
              <Input
                label={t('login.username')}
                // Kirish sahifasida boshqa nishon yo'q — kursor darhol birinchi
                // maydonda turadi va klaviaturadan uzilish bo'lmaydi.
                autoFocus
                maxLength={60}
                autoComplete="username"
                autoCapitalize="off"
                spellCheck={false}
                error={errors.username?.message}
                {...usernameField}
                onChange={(event) => {
                  // Nusxa-ko'chirishda tushgan bo'shliq va tutuq belgilari
                  // maydonga kirmaydi — server baribir faqat harf va raqamni
                  // qabul qiladi.
                  event.target.value = event.target.value.replace(/[^A-Za-z0-9]/g, '').slice(0, 60);
                  void usernameField.onChange(event);
                }}
              />

              <PasswordInput
                label={t('login.password')}
                autoComplete="current-password"
                error={errors.password?.message}
                {...register('password')}
              />

              <CaptchaField captcha={captcha} />
            </div>

            <Button
              type="submit"
              variant="primary"
              size="lg"
              loading={loading}
              disabled={captcha.token === null}
              className="mt-7 w-full"
            >
              {t('login.submit')}
            </Button>

            {/* Ajratgich — ikki yo'l TENG huquqli ekanini ko'rsatadi.
                Telegram tugmasi formaning ICHIDA turadi, lekin `type="button"`
                bo'lgani uchun uni yubormaydi. */}
            <div className="my-5 flex items-center gap-3">
              <span className="bg-border h-px flex-1" aria-hidden />
              <span className="text-text-faint text-xs">{t('login.or')}</span>
              <span className="bg-border h-px flex-1" aria-hidden />
            </div>

            <TelegramLoginButton />
          </form>
        </div>
      </main>
    </div>
  );
};
